import { Suspense, useEffect } from "react";
import { Redirect, Route, Switch, useHistory, useLocation } from "react-router-dom";
import styled from "styled-components";
import ScrollToTop, { ScrollToTopOnNavigate } from "../common/ScrollToTop";
import { BrandLogo } from "../common/BrandLogo";
import Atmosphere from "../components/Atmosphere";
import Footer from "../components/Footer";
import Header from "../components/Header";
import NotFound from "../pages/NotFound";
import { Styles } from "../styles/styles";
import { color, font, layer, size } from "../styles/theme";
import routes from "./config";
import { redirectFor } from "./redirects";

/**
 * Rescues links made while the site was on HashRouter.
 *
 * A bookmarked `/#/terms` arrives here as pathname "/" with hash "#/terms",
 * which would silently render the home page. Rewriting it once on mount turns
 * it into a real navigation. Runs before paint via useLayoutEffect semantics
 * isn't needed — the redirect is a replace, so nothing flashes in practice.
 */
const useHashLegacyRedirect = () => {
  const history = useHistory();

  useEffect(() => {
    const { hash, pathname } = window.location;
    if (pathname !== "/" || !hash.startsWith("#/")) return;

    const target = hash.slice(1); // "#/terms" -> "/terms"
    if (target === "/") {
      history.replace("/");
      return;
    }
    history.replace(redirectFor(target) ?? target);
  }, [history]);
};

/** Sits above the atmosphere layer so the fog stays behind the content. */
const Main = styled("main")`
  position: relative;
  z-index: ${layer.content};
`;

const Loading = styled("div")`
  min-height: 70vh;
  display: grid;
  place-items: center;
  gap: 1rem;
  color: ${color.smoke};
  font-family: ${font.label};
  font-size: ${size.label};
  letter-spacing: 0.3em;
  text-transform: uppercase;

  img {
    opacity: 0.6;
    animation: ghost-flicker 3s linear infinite;
  }
`;

const Fallback = () => (
  <Loading role="status" aria-live="polite">
    <BrandLogo height={26} alt="" />
    Loading
  </Loading>
);

/**
 * Last resort in the Switch: send aliases and retired paths to their canonical
 * route, and show a real 404 for anything genuinely unknown.
 *
 * The search string and hash are carried across, so ?utm_source on an old link
 * survives the redirect.
 */
const CatchAll = () => {
  const location = useLocation();
  const target = redirectFor(location.pathname);

  if (!target) return <NotFound />;
  return <Redirect to={{ ...location, pathname: target }} />;
};

const Router = () => {
  const { pathname } = useLocation();
  useHashLegacyRedirect();

  // Routes that opt out of the marketing header and footer.
  const bare = routes.some(
    (route) => route.chrome === false && route.path === pathname
  );

  return (
    <>
      <Styles />
      <Atmosphere />
      <ScrollToTopOnNavigate />
      {!bare && <Header />}

      <Main id="main" tabIndex={-1}>
        <Suspense fallback={<Fallback />}>
          <Switch>
            {routes.map((route) => (
              <Route
                key={route.name}
                path={route.path}
                exact={route.exact}
                component={route.component}
              />
            ))}

            {/* Aliases and retired paths resolve to their canonical route.
                Placed after the real routes so a canonical path never pays
                for this lookup. */}
            <Route component={CatchAll} />
          </Switch>
        </Suspense>
      </Main>

      {!bare && <Footer />}
      {!bare && <ScrollToTop />}
    </>
  );
};

export default Router;
